import React, { useState, useEffect, useRef } from 'react';

import { uploadImageToCloudinaryWithProgress } from '../../../../api/external/cloudinary/upload';
import {
  checkAvailability,
  verifyCredentials,
} from '../../../../services/userService';

import {
  validatePassword,
  allPasswordRules,
} from '../../../../utils/validation/passwordValidation';

import EditModalShell from '../forms/EditModalShell/EditModalShell';
import LoadingModal from '../LoadingModal/LoadingModal';
import AdminProgressBar from '../../AdminProgressBar/AdminProgressBar';
import ErrorMessage from '../../../shared/ErrorMessage/ErrorMessage';
import ErrorMessages from '../../../shared/ErrorMessages/ErrorMessages';
import ImageInputWithPreview from '../forms/ImageInputWithPreview/ImageInputWithPreview';
import FormField from '../forms/FormField/FormField';

import './UserModal.css';

const UserModal = ({ onClose, onSubmit, user, mode }) => {
  const initialFormState = {
    userName: '',
    email: '',
    isAdmin: false,
    password: '',
    imageUrl: '', // image path
  };

  const [formData, setFormData] = useState(initialFormState);
  const [selectedFile, setSelectedFile] = useState(null); // raw file
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [userModalErrorMessage, setUserModalErrorMessage] = useState('');
  const [userModalPasswordErrorMessages, setUserModalPasswordErrorMessages] =
    useState(allPasswordRules);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const { userName, email, isAdmin, password, imageUrl } = formData;

  const firstInputRef = useRef();

  useEffect(() => {
    setFormData({
      userName: user?.userName || '',
      email: user?.email || '',
      isAdmin: user?.isAdmin || false,
      password: user?.password || '',
      imageUrl: user?.imageUrl || '', // image path
    });
    setSelectedFile(null);
    setImagePreviewUrl(user?.imageUrl || '');

    firstInputRef.current?.focus();
  }, [mode, user]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePasswordChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setUserModalPasswordErrorMessages(validatePassword(e.target.value));
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, imageUrl: '' }));
    setSelectedFile(null);
    setImagePreviewUrl(null);
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      if (mode === 'add') {
        const { userName, email } = formData;
        await checkAvailability({ userName, email });
      }

      if (mode === 'edit') {
        const { userName, email, password } = formData;
        const credentials = { userName, email, password };
        await verifyCredentials(user._id, credentials);
      }

      setLoading(!!selectedFile);
      setProgress(0);

      let uploadedData = {
        url: formData.imageUrl,
        publicId: user?.imagePublicId,
      };

      if (selectedFile) {
        uploadedData = await uploadImageToCloudinaryWithProgress(
          selectedFile,
          setProgress,
          'users',
          [0, 100],
        );
      }

      setProgress(100);

      const finalData = {
        ...formData,
        imageUrl: uploadedData.url || '',
        imagePublicId: uploadedData.publicId || '',
      };

      await onSubmit(finalData);
    } catch (err) {
      setUserModalErrorMessage(err.message);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <EditModalShell
      formId='user-modal-form'
      onClose={onClose}
      onSubmit={handleSubmit}
      mode={mode}
      isLoading={loading}
      isSubmitDisabled={
        mode === 'add' && userModalPasswordErrorMessages.length > 0
      }
      itemLabel='User'
    >
      {loading && (
        <LoadingModal>
          <AdminProgressBar progress={progress} />
        </LoadingModal>
      )}
      <ImageInputWithPreview
        imagePreviewUrl={imagePreviewUrl}
        imageUrl={imageUrl}
        onFileChange={handleFileChange}
        onRemoveImage={handleRemoveImage}
        previewShape='circle'
      />

      <ErrorMessage>{userModalErrorMessage}</ErrorMessage>

      {mode === 'add' && (
        <ErrorMessages
          errorsTitle='Password must have:'
          errors={allPasswordRules}
          currentErrors={userModalPasswordErrorMessages}
        />
      )}

      <FormField
        type='text'
        name='userName'
        label='Username'
        value={userName}
        onChange={handleInputChange}
        ref={firstInputRef}
        required
      />

      <FormField
        type='password'
        name='password'
        label='Password'
        value={password}
        onChange={handlePasswordChange}
        autoComplete='new-password'
        required
      />

      <FormField
        type='email'
        name='email'
        label='Email Address'
        value={email}
        onChange={handleInputChange}
        autoComplete='on'
        required
      />

      <FormField
        type='checkbox'
        name='isAdmin'
        label='Admin'
        value={isAdmin}
        checked={isAdmin}
        onChange={handleCheckboxChange}
      />
    </EditModalShell>
  );
};

export default UserModal;
